<template>
    <div class="container-fluid active-event-page">
        <div class="page-heading d-sm-flex align-items-center justify-content-between mb-4">
            <h1 class="h3 mb-0 text-gray-800">Active event</h1>
            <div class="page-actions mt-3 mt-sm-0" v-if="event">
                <RouterLink :to="{ path: '/event-detail/add', query: { eventId: event.id } }" class="btn btn-primary">
                    <i class="pi pi-plus mr-2" aria-hidden="true"></i>Add session
                </RouterLink>
                <RouterLink :to="{ path: '/event/edit/' + event.id }" class="btn btn-outline-secondary">Edit event</RouterLink>
            </div>
        </div>

        <div v-if="loading" class="page-loading" role="status" aria-live="polite">
            <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
            <span>Loading active event...</span>
        </div>

        <div v-else-if="!event" class="empty-state-card">
            <i class="pi pi-calendar-plus" aria-hidden="true"></i>
            <h2>No active event</h2>
            <p>Choose an event to make its programme and sessions available here.</p>
        </div>

        <div v-else>
            <section class="event-overview-card mb-4" aria-label="Active event summary">
                <div class="event-overview-header">
                    <h2>{{ event.name }}</h2>
                    <span class="badge badge-status" :class="statusBadgeClass(event.status)">
                        {{ event.statusLabel || computeEventStatus(event) }}
                    </span>
                </div>
                <div class="event-meta-list">
                    <span><i class="pi pi-calendar" aria-hidden="true"></i>{{ fmtRange(event.startDate, event.endDate) }}</span>
                    <span v-if="event.locationName"><i class="pi pi-map-marker" aria-hidden="true"></i>{{ event.locationName }}</span>
                </div>
                <p class="event-description" v-if="event.description">{{ event.description }}</p>
                <div class="event-stats">
                    <div><span>Sessions</span><strong>{{ totalSessions }}</strong></div>
                    <div><span>Speakers</span><strong>{{ totalSpeakers }}</strong></div>
                    <div><span>Attended</span><strong>{{ totalAttendees }}</strong></div>
                </div>
            </section>

            <div class="schedule-filter mb-4" v-if="dateGroups.length > 1">
                <ul class="date-tabs" role="tablist" aria-label="Programme date">
                    <li v-for="d in dateGroups" :key="d.date" role="presentation">
                        <button type="button"
                                role="tab"
                                :id="'date-tab-' + d.date"
                                :aria-selected="activeDate === d.date ? 'true' : 'false'"
                                :aria-controls="'date-panel-' + d.date"
                                :tabindex="activeDate === d.date ? 0 : -1"
                                :class="['date-tab', activeDate === d.date ? 'active' : '']"
                                @click="activeDate = d.date"
                                @keydown.left.prevent="focusTab(-1)"
                                @keydown.right.prevent="focusTab(1)">
                            {{ fmtTabDate(d.date) }}
                            <span v-if="isToday(d.date)" class="today-dot">Today</span>
                        </button>
                    </li>
                </ul>
            </div>

            <section v-for="group in filteredDateGroups"
                     :key="group.date"
                     class="session-group mb-4"
                     role="tabpanel"
                     :id="'date-panel-' + group.date"
                     :aria-labelledby="'date-tab-' + group.date">
                <div class="session-group-heading">
                    <h2>{{ fmtTabDate(group.date) }}</h2>
                    <span>{{ group.items.length }} session<span v-if="group.items.length !== 1">s</span></span>
                </div>

                <ol class="timeline">
                    <li v-for="session in group.items" :key="session.id"
                        class="timeline-row" :class="{ 'is-main': session.category == 1 }">
                        <div class="timeline-time">
                            <span class="time-start">{{ fmtTime(session.startDate) }}</span>
                            <span class="time-end">
                                {{ fmtTime(session.endDate) }}<span v-if="dayOffset(session)"
                                      class="time-nextday"
                                      :class="{ 'is-suspect': dayOffset(session) > 1 }"
                                      :title="'Ends ' + dayOffset(session) + ' day(s) later, on ' + fmtTabDate(session.endDate)">+{{ dayOffset(session) }}</span>
                            </span>
                        </div>

                        <div class="timeline-marker">
                            <img v-if="iconUrl(session)"
                                 class="session-icon"
                                 :src="iconUrl(session)"
                                 alt=""
                                 loading="lazy"
                                 @error="onIconError(session.id)">
                            <span v-else class="marker-dot" aria-hidden="true"></span>
                        </div>

                        <article class="timeline-card">
                            <div class="card-top">
                                <h3>
                                    <span v-if="session.category == 1" class="tag-main">Main</span>
                                    {{ session.name }}
                                </h3>
                                <div class="row-actions">
                                    <button type="button" class="btn btn-outline-secondary btn-sm"
                                            :aria-label="'Edit ' + session.name" v-tooltip="'Edit'"
                                            @click="onEditSession(session)"><i class="pi pi-pencil" aria-hidden="true"></i></button>
                                    <button type="button" class="btn btn-outline-secondary btn-sm"
                                            :aria-label="'Delete ' + session.name" v-tooltip="'Delete'"
                                            @click="onDeleteSession(session)"><i class="pi pi-trash" aria-hidden="true"></i></button>
                                </div>
                            </div>

                            <p class="card-location" v-if="session.locationName">
                                <i class="pi pi-map-marker" aria-hidden="true"></i>{{ session.locationName }}
                            </p>

                            <p class="card-desc" v-if="sessionDescription(session)">
                                {{ truncate(sessionDescription(session), 180) }}
                            </p>

                            <div class="card-foot" v-if="session.category == 1">
                                <p class="card-counts">
                                    {{ session.speakers?.length || 0 }} speaker<span v-if="(session.speakers?.length || 0) !== 1">s</span>
                                    <span class="dot" aria-hidden="true">·</span>
                                    <button type="button" class="link-count" @click="onShowAttendees(session)">
                                        {{ session.memberScanedCount || 0 }} attended
                                    </button>
                                    <span class="dot" aria-hidden="true">·</span>
                                    <RouterLink :to="{ path: '/forum-question', query: { eventDetailId: session.id } }" class="link-count">
                                        Questions
                                    </RouterLink>
                                </p>
                                <ul class="speakers-list" v-if="session.speakers && session.speakers.length">
                                    <li v-for="sp in session.speakers" :key="sp.id">
                                        <button type="button" class="speaker-chip" @click="onShowSpeaker(sp)">{{ sp.name }}</button>
                                    </li>
                                </ul>
                            </div>
                        </article>
                    </li>
                </ol>
            </section>
        </div>

        <ConfirmDialog></ConfirmDialog>

        <Dialog v-model:visible="showSpeakerDialog" modal :header="selectedSpeaker.name" :style="{ width: '50vw' }">
            <SpeakerCardView :speaker="selectedSpeaker" v-if="selectedSpeaker && selectedSpeaker.id"></SpeakerCardView>
        </Dialog>

        <Dialog v-model:visible="showAttendeeDialog" modal :header="attendeeDialogHeader" :style="{ width: '60vw' }">
            <table class="table table-striped table-sm mb-0">
                <thead>
                    <tr>
                        <th style="width:50px">#</th>
                        <th>Name</th>
                        <th>Company</th>
                        <th>Scanned at</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="attendees.length === 0">
                        <td colspan="4" class="text-center text-muted">No one has been scanned into this session yet.</td>
                    </tr>
                    <tr v-for="(a, idx) in attendees" :key="a.id">
                        <td>{{ idx + 1 }}</td>
                        <td class="text-left">{{ a.name }}</td>
                        <td class="text-left">{{ a.organizationName }}</td>
                        <td class="text-left">{{ fmtDateTime(a.scannedAt) }}</td>
                    </tr>
                </tbody>
            </table>
        </Dialog>
    </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapGetters } from 'vuex'
import ConfirmDialog from 'primevue/confirmdialog';
import Dialog from 'primevue/dialog';
import SpeakerCardView from '@/components/master/SpeakerCardView.vue';
import apiService from '@/services/ApiService';
import moment from 'moment';

export default defineComponent({
    name: 'EventActive2',
    components: { ConfirmDialog, Dialog, SpeakerCardView },
    data() {
        return {
            loading: false,
            sessions: [],
            activeDate: null,
            showSpeakerDialog: false,
            showAttendeeDialog: false,
            selectedSpeaker: { id: '', name: '', email: '', companyName: '' },
            selectedSessionName: '',
            // Session ids whose image 404'd. The filename in the database can outlive
            // the file on disk, and a broken-image box is worse than no image at all.
            failedIcons: [],
            attendees: []
        };
    },
    computed: {
        ...mapGetters(['getActiveEvent']),
        event() { return this.getActiveEvent; },
        dateGroups() {
            const map = {};
            this.sessions.forEach(s => {
                const d = this.onlyDate(s.startDate);
                if (!map[d]) map[d] = [];
                map[d].push(s);
            });
            return Object.keys(map).sort().map(d => ({
                date: d,
                items: map[d].sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
            }));
        },
        filteredDateGroups() {
            if (!this.activeDate) return this.dateGroups;
            return this.dateGroups.filter(g => g.date === this.activeDate);
        },
        totalSessions() { return this.sessions.length; },
        // Counts people, not appearances — one speaker across five sessions is one speaker.
        totalSpeakers() {
            const ids = new Set();
            this.sessions.forEach(s => (s.speakers || []).forEach(sp => ids.add(sp.id)));
            return ids.size;
        },
        totalAttendees() { return this.sessions.reduce((sum, s) => sum + (s.memberScanedCount || 0), 0); },
        attendeeDialogHeader() {
            return this.selectedSessionName ? `Attended · ${this.selectedSessionName}` : 'Attended';
        }
    },
    methods: {
        loadData() {
            this.loading = true;
            this.$store.dispatch('fetchActiveEvent')
                .catch(err => {
                    console.log(err);
                    this.$toast.add({ severity: 'error', summary: 'Failed to get active event', detail: err.response?.data?.Message, life: 5000 });
                })
                .finally(() => { this.loadSessions(); });
        },
        loadSessions() {
            apiService.get('/eventdetail')
                .then(res => {
                    this.sessions = (res.data || []).sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
                    if (!this.activeDate && this.sessions.length) {
                        this.activeDate = this.onlyDate(this.sessions[0].startDate);
                    }
                })
                .catch(err => {
                    console.log(err);
                    this.$toast.add({ severity: 'error', summary: 'Failed to get sessions', detail: err.response?.data?.Message, life: 5000 });
                })
                .finally(() => { this.loading = false; });
        },
        fmtRange(start, end) {
            if (!start) return '';
            const s = moment(start), e = moment(end);
            if (!end) return s.format('D MMM YYYY');
            if (s.isSame(e, 'month')) return `${s.format('D')} – ${e.format('D MMM YYYY')}`;
            if (s.isSame(e, 'year')) return `${s.format('D MMM')} – ${e.format('D MMM YYYY')}`;
            return `${s.format('D MMM YYYY')} – ${e.format('D MMM YYYY')}`;
        },
        fmtTabDate(d) { return d ? moment(d).format('MMM DD, YYYY') : ''; },
        fmtTime(d) { return d ? moment(d).format('HH:mm') : ''; },
        fmtDateTime(d) { return d ? moment(d).format('DD/MM/YYYY HH:mm') : ''; },
        onlyDate(d) { return d ? moment(d).format('YYYY-MM-DD') : ''; },
        truncate(text, len) { if (!text) return ''; return text.length > len ? text.substring(0, len) + '...' : text; },
        // The date lives in the group heading, so a session only needs a date marker
        // when it ends on a later day than it starts. Real data has sessions off by a
        // week (a mistyped end date), so show the actual offset rather than assuming
        // "+1" — a "+7" is how someone notices the row is wrong.
        dayOffset(session) {
            if (!session.startDate || !session.endDate) return 0;
            const start = moment(session.startDate).startOf('day');
            const end = moment(session.endDate).startOf('day');
            return Math.max(0, end.diff(start, 'days'));
        },
        // Many sessions carry a description that just repeats the title. Printing it
        // gives every card a line that says nothing.
        sessionDescription(session) {
            const desc = (session.description || '').trim();
            if (!desc) return '';
            const normalise = t => t.toLowerCase().replace(/[.\s]+$/, '').trim();
            return normalise(desc) === normalise(session.name || '') ? '' : desc;
        },
        // The "Session image" uploaded on the session form. In practice these are
        // category pictures reused across sessions (hod.jpg, reg.jpg, lunch.jpg), so
        // they work as icons on the rail rather than as photos of one session.
        iconUrl(session) {
            const file = (session.imageNames || [])[0];
            if (!file || this.failedIcons.includes(session.id)) return '';
            return `${apiService.defaults.baseURL}/file/${file}`;
        },
        onIconError(id) {
            if (!this.failedIcons.includes(id)) this.failedIcons.push(id);
        },
        focusTab(step) {
            const dates = this.dateGroups.map(g => g.date);
            const next = dates[dates.indexOf(this.activeDate) + step];
            if (!next) return;
            this.activeDate = next;
            this.$nextTick(() => {
                const el = document.getElementById('date-tab-' + next);
                if (el) el.focus();
            });
        },
        statusBadgeClass(status) {
            switch (status) {
                case 1: return 'badge-success'; // running
                case 2: return 'badge-warning'; // upcoming
                case 3: return 'badge-secondary'; // finished
                default: return 'badge-info';
            }
        },
        isToday(dateStr) {
            if (!dateStr) return false;
            return moment().format('YYYY-MM-DD') === moment(dateStr).format('YYYY-MM-DD');
        },
        computeEventStatus(e) {
            if (!e) return '';
            const now = moment();
            if (moment(e.startDate).isAfter(now)) return 'Upcoming';
            if (moment(e.endDate).isBefore(now)) return 'Finished';
            return 'Ongoing';
        },
        onEditSession(session) {
            this.$router.push(`/event-detail/edit/${session.id}`);
        },
        onDeleteSession(session) {
            this.$confirm.require({
                message: `Delete "${session.name}"? This cannot be undone.`,
                header: 'Delete session',
                icon: 'pi pi-exclamation-triangle',
                accept: () => { this.doDeleteSession(session.id); }
            });
        },
        doDeleteSession(id) {
            apiService.delete(`/eventdetail/${id}`)
                .then(() => {
                    this.$toast.add({ severity: 'success', summary: 'Deleted', detail: 'Session deleted', life: 5000 });
                    this.loadSessions();
                })
                .catch(err => {
                    console.log(err);
                    this.$toast.add({ severity: 'error', summary: 'Failed to delete session', detail: err.response?.data?.Message, life: 5000 });
                });
        },
        onShowSpeaker(sp) {
            this.selectedSpeaker = sp; this.showSpeakerDialog = true; this.showAttendeeDialog = false;
        },
        onShowAttendees(session) {
            this.attendees = [];
            this.selectedSessionName = session.name;
            this.showAttendeeDialog = true;
            this.showSpeakerDialog = false;
            apiService.get(`/eventdetail/${session.id}/attendees`)
                .then(res => { this.attendees = res.data || []; })
                .catch(err => { this.$toast.add({ severity: 'error', summary: 'Failed to get attendees', detail: err.response?.data?.Message, life: 5000 }); });
        }
    },
    mounted() { this.loadData(); }
});
</script>

<style scoped>
.active-event-page { max-width: 1120px; }
.page-heading { border-bottom: 1px solid #e2e6ea; padding-bottom: 1rem; }
.page-actions { display: flex; gap: .5rem; flex-wrap: wrap; }

.page-loading { align-items: center; color: #5a6270; display: flex; gap: .7rem; font-size: .92rem; min-height: 200px; justify-content: center; }
.page-loading .pi { color: #4e73df; font-size: 1.2rem; }

.event-overview-card, .session-group, .empty-state-card { background: #fff; border: 1px solid #e2e6ec; border-radius: .65rem; box-shadow: 0 .15rem .65rem rgba(58, 59, 69, .05); }
.event-overview-card { padding: 1.5rem; }
.event-overview-header { align-items: flex-start; display: flex; gap: 1rem; justify-content: space-between; }
.event-overview-header h2 { color: #2b2f36; font-size: 1.35rem; font-weight: 600; line-height: 1.3; margin: 0; text-transform: none; }
.badge-status { flex: none; font-size: .75rem; padding: .45rem .65rem; }
.event-meta-list { border-bottom: 1px solid #edf0f4; display: flex; flex-wrap: wrap; gap: .75rem 1.5rem; margin-top: 1rem; padding-bottom: 1rem; }
.event-meta-list span { align-items: center; color: #5a6270; display: inline-flex; font-size: .88rem; }
.event-meta-list i { color: #4e73df; margin-right: .45rem; }
.event-description { color: #5a6270; font-size: .9rem; margin: 1rem 0 0; max-width: 68ch; }
.event-stats { display: flex; flex-wrap: wrap; gap: .75rem; margin-top: 1.25rem; }
.event-stats > div { background: #f7f9fc; border: 1px solid #edf0f4; border-radius: .45rem; min-width: 112px; padding: .7rem .85rem; }
.event-stats span { color: #5a6270; display: block; font-size: .78rem; font-weight: 600; }
.event-stats strong { color: #2b2f36; display: block; font-size: 1.25rem; line-height: 1.2; margin-top: .2rem; }

.date-tabs { display: flex; flex-wrap: wrap; gap: .25rem; list-style: none; margin: 0; padding: 0; }
.date-tab { background: transparent; border: 1px solid transparent; border-radius: .4rem; color: #4e73df; font-size: .88rem; padding: .5rem .8rem; }
.date-tab:hover { background: #f1f4fb; }
.date-tab.active { background: #4e73df; border-color: #4e73df; color: #fff; font-weight: 600; }
.date-tab:focus-visible { box-shadow: 0 0 0 .2rem rgba(78, 115, 223, .4); outline: none; }
.today-dot { background: rgba(255, 255, 255, .22); border-radius: .3rem; font-size: .68rem; margin-left: .4rem; padding: .1rem .3rem; }
.date-tab:not(.active) .today-dot { background: #e8edfb; }

.session-group { padding: 1.25rem 1.5rem 1.5rem; }
.session-group-heading { align-items: baseline; border-bottom: 1px solid #edf0f4; display: flex; justify-content: space-between; margin-bottom: 1.25rem; padding-bottom: .9rem; }
.session-group-heading h2 { color: #2b2f36; font-size: 1.08rem; font-weight: 600; margin: 0; }
.session-group-heading > span { color: #5a6270; font-size: .82rem; }

.timeline { list-style: none; margin: 0; padding: 0; }
.timeline-row { display: grid; gap: 0 .8rem; grid-template-columns: 3.6rem 2.25rem 1fr; }
.timeline-time { color: #5a6270; padding: .35rem 0 1.5rem; text-align: right; }

/* The marker column carries both the rail and the session's icon, so the rail
   reads as a strip of what the day is made of rather than a row of dots. */
.timeline-marker { align-items: flex-start; display: flex; justify-content: center; position: relative; }
.timeline-row:not(:last-child) .timeline-marker::after { background: #e4e8ef; bottom: -0.35rem; content: ''; left: calc(50% - 1px); position: absolute; top: 2.55rem; width: 2px; }
/* Sits on the centre line of the title's first line, where the icon's top half does. */
.marker-dot { background: #c2c9d6; border-radius: 50%; height: .5rem; margin-top: .45rem; width: .5rem; }
.timeline-row.is-main .marker-dot { background: #4e73df; box-shadow: 0 0 0 3px #e8edfb; }
.session-icon { background: #f5f7fa; border: 1px solid #e4e8ef; border-radius: .35rem; height: 2.25rem; object-fit: cover; width: 2.25rem; }
.timeline-row.is-main .session-icon { border-color: #4e73df; box-shadow: 0 0 0 2px #e8edfb; }
.time-start { color: #2b3038; display: block; font-size: .95rem; font-weight: 700; font-variant-numeric: tabular-nums; }
.time-end { color: #6b7280; display: block; font-size: .8rem; font-variant-numeric: tabular-nums; }
.time-nextday { color: #4e73df; font-size: .68rem; font-weight: 700; margin-left: .15rem; vertical-align: .25rem; }
/* A session spanning more than one night is almost always a mistyped end date.
   Flag it instead of rendering it as a normal overnight. */
.time-nextday.is-suspect { background: #fdecea; border-radius: .2rem; color: #b42318; padding: 0 .2rem; }

.timeline-card { padding: 0 0 1.5rem; }
.card-top { align-items: flex-start; display: flex; gap: 1rem; justify-content: space-between; }
.timeline-card h3 { color: #2b3038; font-size: 1rem; font-weight: 600; line-height: 1.4; margin: 0; }
.tag-main { background: #e8edfb; border-radius: .25rem; color: #3a5bc7; font-size: .7rem; font-weight: 700; margin-right: .35rem; padding: .15rem .35rem; vertical-align: .08rem; }
.row-actions { display: flex; flex: none; gap: .3rem; opacity: 0; transition: opacity .15s ease; }
.timeline-row:hover .row-actions, .row-actions:focus-within { opacity: 1; }
.card-location { align-items: center; color: #6b7280; display: flex; font-size: .82rem; margin: .3rem 0 0; }
.card-location i { color: #9aa2b1; margin-right: .3rem; }
.card-desc { color: #5a6270; font-size: .86rem; line-height: 1.5; margin: .5rem 0 0; max-width: 68ch; }

.card-foot { margin-top: .65rem; }
.card-counts { color: #5a6270; font-size: .82rem; margin: 0; }
.card-counts .dot { margin: 0 .4rem; }
.link-count { background: none; border: 0; color: #4e73df; font-size: .82rem; padding: 0; text-decoration: underline; }
.link-count:hover { color: #2e59d9; }
.link-count:focus-visible { box-shadow: 0 0 0 .2rem rgba(78, 115, 223, .4); border-radius: .2rem; outline: none; }
.speakers-list { display: flex; flex-wrap: wrap; gap: .3rem; list-style: none; margin: .5rem 0 0; padding: 0; }
.speaker-chip { background: #f5f7fa; border: 1px solid #e4e8ef; border-radius: .25rem; color: #4f5762; font-size: .74rem; padding: .15rem .4rem; }
.speaker-chip:hover { background: #e8edfb; border-color: #cfd9f5; color: #3a5bc7; }
.speaker-chip:focus-visible { box-shadow: 0 0 0 .2rem rgba(78, 115, 223, .4); outline: none; }

.empty-state-card { color: #5a6270; padding: 3rem 1.5rem; text-align: center; }
.empty-state-card > i { color: #4e73df; font-size: 1.8rem; }
.empty-state-card h2 { color: #2b2f36; font-size: 1.1rem; font-weight: 600; margin: .8rem 0 .35rem; }
.empty-state-card p { font-size: .9rem; margin: 0; }

/* Touch devices have no hover, so the row actions must stay visible. */
@media (hover: none) {
    .row-actions { opacity: 1; }
}

@media (max-width: 576px) {
    .event-overview-card, .session-group { padding: 1rem; }
    .event-overview-header { align-items: flex-start; flex-direction: column; }
    .event-stats > div { flex: 1; min-width: 90px; }
    /* No room for a rail at phone width: the icon moves beside the time and the
       times stack into one line. */
    .timeline-row { grid-template-columns: 1.75rem 1fr; gap: 0 .6rem; }
    .timeline-time { align-items: baseline; display: flex; gap: .5rem; grid-column: 2; order: 2; padding: 0 0 .35rem; text-align: left; }
    .timeline-marker { align-items: flex-start; grid-column: 1; grid-row: 1 / span 2; padding-top: .1rem; }
    .timeline-row:not(:last-child) .timeline-marker::after { display: none; }
    .session-icon { height: 1.75rem; width: 1.75rem; }
    .timeline-card { grid-column: 2; order: 3; }
    .time-start, .time-end { display: inline; }
    .timeline-card { border-bottom: 1px solid #edf0f4; margin-bottom: 1rem; padding-bottom: 1rem; }
    .row-actions { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
    .row-actions { transition: none; }
}
</style>
